"use client";

import { useState } from "react";

import { Container } from "@/app/(marketing)/_components/ui/container";

import { SectionHeading } from "./dp-section";

const STEPS = [
  {
    number: "01",
    title: "Map your workflows",
    description:
      "In one short conversation, we map the workflows your teams run and where your business has real depth. No integrations and no change to how anyone works.",
  },
  {
    number: "02",
    title: "We design the partnership",
    description:
      "Together we define which workflows are in scope, how they are anonymized, and the boundaries your business requires, all agreed in writing before anything begins.",
  },
  {
    number: "03",
    title: "Your team works as usual",
    description:
      "Your teams keep doing their normal work. Approved examples of that work are verified for quality before they count, so only real, accurate work is ever paid.",
  },
  {
    number: "04",
    title: "You get paid per completion",
    description:
      "Payouts land monthly on verified completions. You decide what your people earn and what the business keeps as margin.",
  },
];

// Interactive column accordion: hovering (or focusing) a step expands it and
// reveals its detail while the others collapse to a compact number + title.
// Falls back to full, stacked cards on mobile where there is no hover.
export function DpHowItWorks() {
  const [active, setActive] = useState(0);

  return (
    <section id="how-it-works" className="scroll-mt-24 py-16 md:py-20">
      <Container className="max-w-[1100px]!">
        <SectionHeading
          eyebrow="How it works"
          title="Four steps to a new revenue line."
          intro="You scope your workflows once. We handle everything after, and the work stays inside your teams' normal day."
        />

        <div className="flex flex-col gap-3 lg:h-[300px] lg:flex-row lg:gap-3">
          {STEPS.map((step, i) => {
            const isActive = active === i;
            return (
              <button
                key={step.number}
                type="button"
                onMouseEnter={() => {
                  setActive(i);
                }}
                onFocus={() => {
                  setActive(i);
                }}
                onClick={() => {
                  setActive(i);
                }}
                aria-expanded={isActive}
                className={`group flex min-w-0 flex-col justify-between rounded-2xl p-6 text-left transition-[background-color,flex] duration-300 ease-out lg:p-7 ${
                  isActive
                    ? "bg-marketing-blue lg:flex-[2.6]"
                    : "bg-marketing-cream-light lg:flex-1"
                }`}
              >
                <span
                  className={`font-mono text-[13px] tracking-[0.04em] tabular-nums transition-colors duration-300 ${
                    isActive ? "text-white/70" : "text-marketing-blue"
                  }`}
                >
                  {step.number}
                </span>

                <div className="mt-6 flex flex-col gap-2">
                  <h3
                    className={`text-heading-16! font-medium! transition-colors duration-300 lg:w-[105px] xl:w-[120px] ${
                      isActive ? "text-white" : "text-marketing-text"
                    }`}
                  >
                    {step.title}
                  </h3>
                  <p
                    className={`text-copy-14 overflow-hidden transition-[color,max-height,opacity] duration-300 ease-out lg:w-[230px] xl:w-[290px] ${
                      isActive
                        ? "max-h-48 text-white/85 opacity-100"
                        : "text-marketing-text-muted max-h-48 opacity-100 lg:max-h-0 lg:opacity-0"
                    }`}
                  >
                    {step.description}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </Container>
    </section>
  );
}
